import React from "react";
import { Link } from "react-router-dom";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";
import BrandLogosSection from "../components/BrandLogosSection";
import AISOBlock from "../components/AISOBlock";
import CinematicServiceHero from "../components/CinematicServiceHero";
import { videoJsonLd } from "../lib/videoSchema";

const HERO_IMAGE = "/projects/custom-joinery-macassar-veneer-high-gloss-detail.webp";

const SCOPE = [
  {
    name: "TV units & feature media walls",
    desc: "Floor-to-ceiling media walls with integrated cable management, hidden storage, fluted or veneered panels, and stone or back-painted glass detailing.",
  },
  {
    name: "Library walls & display shelving",
    desc: "Bookshelves, display niches and full-height library walls in oak, walnut, ash and lacquer — designed to hold real books, art and objects.",
  },
  {
    name: "Wall panelling & ceiling joinery",
    desc: "Fluted, ribbed and book-matched wall panelling, slat ceilings, and acoustic joinery — installed seamlessly across rooms and full apartments.",
  },
  {
    name: "Bespoke beds, headboards & dressers",
    desc: "Upholstered or veneered headboards, platform beds with integrated bedside cabinets and matching dressing tables — built to your exact dimensions.",
  },
  {
    name: "Console tables, vanities & dining furniture",
    desc: "Statement console tables, bathroom vanities, dining tables and credenzas — fabricated in solid wood, veneer, lacquer or marble-topped joinery.",
  },
  {
    name: "Doors, partitions & sliding screens",
    desc: "Pivot doors, pocket doors, fluted-glass partitions and sliding screens — hardware-grade installs with premium ironmongery and concealed runners.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Site survey & design intent",
    body: "We visit the room, take laser measurements, study existing finishes and lighting, and align on the joinery's role — feature wall, storage, partition, or quiet background.",
    image: "/media/team-projects/time-furniture-site-planning-project-coordination.webp",
    imageAlt: "On-site discussion and project coordination during a Time Furniture fit-out",
  },
  {
    n: "02",
    title: "Design, finish samples & itemised BOQ",
    body: "Elevation drawings, 3D renders, finish samples (veneer, lacquer, fluted glass, stone) and an itemised BOQ — carcass, fronts, hardware and stone priced separately.",
    image: "/projects/custom-joinery-macassar-veneer-high-gloss-detail.webp",
    imageAlt: "Macassar veneer high-gloss joinery sample for a custom Dubai project",
  },
  {
    n: "03",
    title: "Manufacturing at our atelier",
    body: "Carcasses, fronts, panels and stone tops manufactured in parallel at our Sharjah workshop. Pre-assembled and quality-checked before they leave.",
    image: "/media/owner-projects/time-furniture-installation-team-joinery-time-furniture.webp",
    imageAlt: "Time Furniture installation team assembling joinery",
  },
  {
    n: "04",
    title: "On-site installation",
    body: "Floor protection, levelling, scribing to existing walls, hardware adjustment and finish touch-up — handled by our in-house joinery team in a single visit where possible.",
    image: "/media/team-projects/time-furniture-finishing-installation-team.webp",
    imageAlt: "Time Furniture installation team completing interior finishing work",
  },
  {
    n: "05",
    title: "Snag, walkthrough & after-care",
    body: "Final adjustment of doors, drawers and soft-close, finish touch-up, and a defects warranty on the joinery, hardware and installation. Adjustment visits in the first year.",
    image: "/projects/sobha-crest-holiday-home-tv-unit-foyer.webp",
    imageAlt: "Completed custom joinery TV unit and foyer handed over at Sobha Crest, Dubai",
  },
];

const JOINERY_TYPES = [
  {
    id: "joinery-media",
    kicker: "Media",
    title: "TV units & media walls",
    desc: "Floor-to-ceiling media walls with hidden storage, fluted detailing and integrated cable management.",
    image: "/projects/address-hotel-2bhk-luxury-living-room-marble-tv-wall.webp",
    alt: "Custom marble media wall with ribbed panelling and integrated TV joinery in a luxury Dubai apartment by Time Furniture",
  },
  {
    id: "joinery-panelling",
    kicker: "Panelling",
    title: "Wall & ceiling panelling",
    desc: "Fluted, ribbed and book-matched veneer panelling — installed seamlessly across rooms and full apartments.",
    image: "/projects/address-downtown-dark-1.webp",
    alt: "Dark veneer wall panelling across a luxury Dubai living space by Time Furniture",
  },
  {
    id: "joinery-storage",
    kicker: "Storage",
    title: "Display & storage walls",
    desc: "Illuminated display cabinets, shelving niches and full-height storage walls for books, art and objects.",
    image: "/media/owner-projects/walk-in-wardrobe-illuminated-glass-fronts-time-furniture.webp",
    alt: "Illuminated glass-front display and storage wall built by Time Furniture",
  },
  {
    id: "joinery-bedroom",
    kicker: "Bedroom",
    title: "Beds, headboards & dressers",
    desc: "Upholstered headboards, platform beds with integrated cabinets and matching dressing tables.",
    image: "/projects/sobha-crest-holiday-home-master-bedroom.webp",
    alt: "Custom upholstered headboard and bedroom joinery at Sobha Crest Dubai by Time Furniture",
  },
  {
    id: "joinery-furniture",
    kicker: "Furniture",
    title: "Consoles, vanities & dining",
    desc: "Statement console tables, bathroom vanities and dining furniture — solid wood, veneer or stone-topped.",
    image: "/projects/custom-joinery-bespoke-console-marble-top.webp",
    alt: "Bespoke marble-top console table built by Time Furniture Dubai",
  },
];

const MATERIALS = [
  { n: "Veneers", d: "Oak · walnut · ash · ebony · macassar" },
  { n: "Lacquer", d: "Matt or high-gloss, any RAL / NCS" },
  { n: "Fluted & glass", d: "Fluted panels · smoke · bronze glass" },
  { n: "Stone tops", d: "Calacatta · Statuario · Dekton · quartz" },
  { n: "Metals", d: "Brass · bronze · blackened steel trims" },
];

const FAQS = [
  {
    q: "What does 'custom joinery' actually cover?",
    a: "Custom joinery is everything built-in or bespoke that isn't off-the-shelf furniture. At Time Furniture this includes wardrobes, walk-in closets, kitchens, TV and media walls, library walls, wall and ceiling panelling, headboards, dressing tables, console tables, vanities, dining furniture, doors, sliding screens and pivot partitions. Cabinetry is manufactured at our own Sharjah atelier and installed by our in-house joinery team.",
  },
  {
    q: "How long does a custom joinery project take in Dubai?",
    a: "A typical custom joinery package — say a feature TV wall plus a media unit — takes 4–6 weeks from approved design to install. Larger packages (full apartment panelling, library walls, multiple feature walls) take 6–10 weeks. We provide a fixed install date in every contract.",
  },
  {
    q: "What materials and finishes do you offer?",
    a: "Real wood veneer (oak, walnut, ash, ebony, macassar), matt and high-gloss lacquer in any RAL or NCS colour, matt PET, fluted and ribbed panels, back-painted glass, mirror, fluted/smoke/bronze glass, stone tops (Calacatta, Statuario, Dekton, quartz, porcelain) and brass / bronze hardware. Finish samples are provided before sign-off.",
  },
  {
    q: "Can you match existing joinery in my home?",
    a: "Yes. We can colour-match veneer or lacquer to existing pieces — please share photos and the original spec if available. For larger projects we'll bring physical samples on site so you compare in your own light, not in the workshop.",
  },
  {
    q: "Do you provide a warranty?",
    a: "Yes. Time Furniture provides a written defects warranty covering joinery, fronts, hinges, drawer channels and installation. Premium hardware (Blum, Hettich, Häfele, Grass, Salice) carries its own manufacturer warranty on top — typically 10+ years on hinges and runners.",
  },
];

const WHY = [
  {
    t: "Our own joinery atelier",
    d: "Carcasses, fronts, panels and stone tops are built at our own Sharjah workshop — full control over quality, lead times and finish consistency.",
  },
  {
    t: "Premium hardware as standard",
    d: "Blum, Hettich, Häfele, Grass and Salice — soft-close hinges and drawer channels designed for long-term daily use, not just opening day.",
  },
  {
    t: "Laser-measured, scribed install",
    d: "Every joinery package is measured on site and scribed to existing wall conditions — so finished work sits flush, with no shadow gaps.",
  },
];

const BRAND_CATEGORIES = [
  {
    label: "Hardware & Accessories",
    groups: [
      {
        brands: [
          { name: "Blum", country: "Austria" },
          { name: "Hettich", country: "Germany" },
          { name: "Häfele", country: "Germany" },
          { name: "Grass", country: "Austria" },
          { name: "Salice", country: "Italy" },
        ],
      },
    ],
  },
];

export default function CustomJoineryDubai() {
  return (
    <ServiceLandingPage
      testid="custom-joinery-dubai-page"
      whatsappText={COMPANY.whatsappJoineryText}
      pageTitle="Custom Joinery Dubai | Bespoke Cabinetry & Panelling UAE — Time Furniture"
      metaDescription="Custom joinery Dubai and bespoke cabinetry UAE — TV walls, panelling, library walls, headboards, vanities and built-in furniture, manufactured at our Sharjah atelier with Blum, Hettich and Häfele hardware. Custom furniture manufacturer UAE since 1993."
      canonical="https://timefurniture.ae/custom-joinery-dubai"
      ogImage={HERO_IMAGE}
      serviceSchemaName="Custom Joinery & Bespoke Cabinetry — Dubai & UAE"
      breadcrumbLabel="Custom Joinery Dubai"
      eyebrow="Custom Joinery · Dubai & UAE"
      h1="Bespoke Joinery &"
      h1Accent="Cabinetry"
      heroImage={HERO_IMAGE}
      heroImageAlt="High-gloss macassar veneer detail on a curved bespoke console cabinet by Time Furniture, Dubai"
      heroOverride={
        <CinematicServiceHero
          mediaKey="joinery-hero"
          posterAlt="Custom marble media wall with ribbed panelling and integrated TV joinery in a Dubai apartment"
          eyebrow="Custom Joinery · Dubai & UAE"
          sectionNumber="03"
          testid="joinery-cinematic-hero"
          h1={
            <h1
              data-testid="custom-joinery-dubai-page-h1"
              className="font-serif-display text-4xl md:text-6xl lg:text-[72px] leading-[1.04] tracking-tight text-[#FAF5EC] max-w-4xl"
            >
              Bespoke Joinery &amp; <em className="text-[#D4A963] not-italic">Cabinetry</em>.
            </h1>
          }
          support={
            <p
              data-testid="custom-joinery-dubai-page-hero-subtext"
              className="mt-7 max-w-2xl text-[#EDE1C8]/90 leading-[1.7] text-base md:text-lg font-light"
            >
              Media walls, panelling and built-in furniture — laser-measured on site, built at our own Sharjah atelier since 1993.
            </p>
          }
        >
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="custom-joinery-dubai-page-hero-cta-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-colors"
            >
              Book a Free Site Visit
            </Link>
            <a
              href="#joinery-showcase"
              data-testid="custom-joinery-dubai-page-hero-cta-showcase"
              className="inline-flex items-center justify-center gap-3 border border-[#D4A963]/70 text-[#D4A963] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C] transition-colors"
            >
              View Our Joinery
            </a>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappJoineryText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="custom-joinery-dubai-page-hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-[#FAF5EC]/55 text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#FAF5EC] hover:text-[#1C110C] transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </CinematicServiceHero>
      }
      extraJsonLd={[
        videoJsonLd({
          key: "joinery-hero",
          name: "Bespoke joinery and cabinetry in Dubai",
          description:
            "Custom media walls, panelling and built-in furniture by Time Furniture — laser-measured, built at our Sharjah atelier and installed across the UAE.",
          seconds: 18,
        }),
      ]}
      intro="Time Furniture is a custom joinery contractor in Dubai delivering bespoke cabinetry, panelling and built-in furniture across the UAE. From feature TV walls and library walls to wall and ceiling panelling, headboards, dressing tables, console tables, vanities and full apartment joinery — every piece is laser-measured on site and built at our own Sharjah atelier in oak, walnut, ash, macassar veneer, lacquer, fluted glass or stone-topped finishes. All carcasses run on premium Blum, Hettich, Häfele, Grass or Salice hardware, so the joinery performs as cleanly on year ten as it does on opening day. We've been a custom furniture manufacturer in the UAE since 1993."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      ctaHeading="Ready to plan your"
      ctaAccent="bespoke joinery"
      ctaSubtext="Book a free site visit. We'll laser-measure the room, propose a layout and reply with a transparent BOQ — carcass, fronts, hardware and stone priced separately."
      afterHero={
        <section id="joinery-showcase" data-testid="joinery-showcase" className="bg-[#FAF5EC] border-b border-[#6E1F2C]/15">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <p className="eyebrow mb-4">What We Build</p>
                <h2 className="font-serif-display text-3xl md:text-5xl text-[#1C110C] max-w-2xl leading-[1.08]">
                  From media walls to dressing rooms.
                </h2>
              </div>
              <nav aria-label="Joinery categories" data-testid="joinery-jump-nav" className="flex flex-wrap gap-2">
                {JOINERY_TYPES.map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    data-testid={`joinery-jump-${t.id}`}
                    className="inline-flex items-center border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A963]"
                  >
                    {t.kicker}
                  </a>
                ))}
              </nav>
            </div>
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-6 gap-4 md:gap-6">
              {JOINERY_TYPES.map((t, i) => (
                <figure
                  key={t.id}
                  id={t.id}
                  data-testid={`joinery-category-${t.id}`}
                  className={`group ${i < 3 ? "lg:col-span-2" : "lg:col-span-3"} ${i === 4 ? "col-span-2 lg:col-span-3" : ""}`}
                >
                  <div className="relative overflow-hidden bg-[#1C110C]">
                    <img
                      src={t.image}
                      alt={t.alt}
                      loading={i < 2 ? "eager" : "lazy"}
                      decoding="async"
                      className={`w-full ${i < 3 ? "aspect-[4/3]" : "aspect-[16/9]"} object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]`}
                    />
                    <span className="absolute top-3 left-3 bg-[#6E1F2C] text-[#FAF5EC] text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">
                      {t.kicker}
                    </span>
                  </div>
                  <figcaption className="mt-4">
                    <p className="font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug">{t.title}</p>
                    <p className="mt-2 text-sm text-[#3E2A22]/85 leading-relaxed max-w-md">{t.desc}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      }
      featureGrid={
        <>
          {/* Materials & finishes strip */}
          <section data-testid="joinery-materials" className="bg-[#1C110C]">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">Materials &amp; Finishes</p>
              <h2 className="mt-3 font-serif-display text-2xl md:text-4xl text-[#FAF5EC] max-w-2xl">
                Sampled in your light, approved before build.
              </h2>
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <figure className="lg:col-span-1" data-testid="joinery-finish-detail">
                  <img
                    src="/projects/custom-joinery-macassar-veneer-high-gloss-detail.webp"
                    loading="lazy"
                    decoding="async"
                    alt="High-gloss macassar veneer finish detail on a curved bespoke console cabinet by Time Furniture"
                    className="w-full aspect-[4/5] object-cover object-center bg-[#2a1a12]"
                  />
                  <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#EDE1C8]/50">
                    Macassar high-gloss finish · bespoke console detail
                  </figcaption>
                </figure>
                <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-2 gap-px bg-[#FAF5EC]/10 self-stretch">
                  {MATERIALS.map((m) => (
                    <div key={m.n} className="bg-[#1C110C] p-5 md:p-6" data-testid={`joinery-material-${m.n.split(" ")[0].toLowerCase()}`}>
                      <p className="font-serif-display text-lg md:text-xl text-[#FAF5EC]">{m.n}</p>
                      <p className="mt-2 text-xs text-[#EDE1C8]/65 leading-relaxed">{m.d}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-6 text-xs text-[#EDE1C8]/50">
                Physical finish samples are brought to your site before sign-off — never approved from screen.
              </p>
            </div>
          </section>
        </>
      }
      aisoBlock={
        <AISOBlock
          testid="joinery-aiso"
          audience={[
            "Villa owners commissioning feature TV walls, library walls or full panelling packages",
            "Apartment owners replacing builder-spec joinery with bespoke cabinetry",
            "Holiday home and Airbnb investors fitting durable, low-maintenance built-ins",
            "Hospitality, clinic and office clients (lounges, lobbies, partner cabins)",
          ]}
          whatWeDo="We design, manufacture and install custom joinery and bespoke cabinetry across Dubai and the wider UAE — including TV and media walls, wall and ceiling panelling, library walls, headboards, dressers, console tables, vanities, dining furniture, doors and sliding partitions. Every piece is built at our Sharjah atelier in veneer, lacquer, fluted glass or stone-topped finishes, and runs on premium Blum, Hettich, Häfele, Grass or Salice hardware."
          scope={[
            "On-site laser measurement & survey",
            "Elevation drawings & finish samples",
            "Carcass, fronts and panel manufacturing",
            "Premium soft-close hinges & runners",
            "Stone, glass & metal detailing",
            "On-site install, scribing & adjustment",
          ]}
          relatedLinks={[
            { kicker: "Wardrobes", label: "Custom Wardrobes Dubai", to: "/custom-wardrobes-dubai" },
            { kicker: "Kitchen", label: "Kitchen Renovation Dubai", to: "/kitchen-renovation-dubai" },
            { kicker: "Furniture", label: "Custom Sofas & Upholstery", to: "/custom-sofas-dubai" },
            { kicker: "Turnkey", label: "Interior Fit-Out Dubai", to: "/interior-fit-out-dubai" },
          ]}
        />
      }
      brandSections={
        <BrandLogosSection
          testid="joinery-brands"
          eyebrow="Brands We Work With"
          heading="Premium Hardware & Appliance Brands We Work With"
          subtext="We specify and install globally trusted hardware, fittings, and appliances based on each project's design, performance requirements, and client preferences."
          categories={BRAND_CATEGORIES}
          disclaimer="Brand selection may vary based on project scope, availability, client preference, and budget."
        />
      }
      internalLinksLine={
        <>
          Also see our{" "}
          <InternalLink to="/custom-wardrobes-dubai">custom wardrobes in Dubai</InternalLink>
          ,{" "}
          <InternalLink to="/kitchen-renovation-dubai">kitchen renovation</InternalLink>
          ,{" "}
          <InternalLink to="/custom-sofas-dubai">custom sofas & upholstery</InternalLink>
          , or our{" "}
          <InternalLink to="/services">full interior fit-out services</InternalLink>
          .
        </>
      }
    />
  );
}
